import { useRef } from 'react';
import type { HistoryData } from '~/vendor/cxy/types';
import { Bar, BarChart, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import styles from './styles/BarChart.module.scss';
import type { BarChartProps } from './util/types';
import { ChartVariant } from './util/types';
import { useBarChart } from './util/useBarChart';
import { useContainerQuery } from './util/useContainerQuery';
import { dateXAxisProps, primaryXAxisProps } from './util/xAxisProps';
import { TabFilter } from './TabFilter';

export function BidirectionalBarChart({
  americanFormat = false,
  barRadius = 0,
  customLabels = { All: 'All', Home: 'Home', Away: 'Away', versus: 'vs' },
  fullTabsWidth = false,
  historyData,
  limit,
  oppositionTeam,
  trackEvent,
  variant = ChartVariant.DEFAULT,
}: Omit<BarChartProps, 'flipBarSuccess' | 'line'>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { changeFilterType, filteredData, maxBetStat, selectedFilterType, shouldShowVersusTab, yAxisWidth } =
    useBarChart({
      historyData,
      limit,
      oppositionTeam,
      trackEvent,
    });

  const yAxisDomain = [-maxBetStat, maxBetStat];

  const isCompact = variant === ChartVariant.COMPACT;
  const isContainerWide = useContainerQuery(containerRef, 769);

  const margin = isCompact ? { top: 0, right: 0, left: 0, bottom: 0 } : undefined;

  return (
    <div className={styles.wrapper} ref={containerRef}>
      <div>
        {!isCompact && (
          <TabFilter
            customLabels={customLabels}
            fullTabsWidth={fullTabsWidth}
            oppositionTeam={oppositionTeam}
            selectedFilterType={selectedFilterType}
            setSelectedFilterType={changeFilterType}
            shouldShowVersusTab={shouldShowVersusTab}
          />
        )}
        <div className={isCompact ? styles.compactBidirectionalBarChartWrapper : styles.bidirectionalBarChartWrapper}>
          <ResponsiveContainer height="100%" width="100%">
            <BarChart barCategoryGap="5%" barGap={0} data={filteredData} margin={margin}>
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--cxy-color-surface-page)',
                  border: 'var(--cxy-border-default)',
                }}
                cursor={{ fill: 'transparent' }}
                formatter={(_, __, props) => {
                  const data = props.payload as HistoryData;
                  if (!data) return null;
                  return [`${data.participant.betResult}`, `${data.awayTeam.name} @ ${data.homeTeam.name}`];
                }}
                labelFormatter={() => ''}
                labelStyle={{
                  color: 'var(--cxy-color-text-default-placeholder)',
                }}
              />
              {!isCompact && (
                <>
                  <XAxis {...primaryXAxisProps({ americanFormat, isContainerWide })} />
                  <XAxis {...dateXAxisProps({ americanFormat, className: 'dateAxis' })} />
                </>
              )}
              <YAxis axisLine={false} domain={yAxisDomain} fontSize={12} tickLine={false} width={yAxisWidth} />
              <Bar dataKey="participant.betStat" radius={barRadius}>
                {filteredData.map((entry, index) => (
                  <Cell className={entry.participant.betStat > 0 ? 'bar-above' : 'bar-below'} key={`cell-${index}`} />
                ))}
              </Bar>
              <ReferenceLine stroke="#666" y={0} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
